/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { difference, isEmpty, map, union } from 'lodash';
import { reaction, toJS } from 'mobx';
import { useObserver } from 'mobx-react';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FB, FBMediaAlbumFieldProps, FBMediaAlbumFieldState, FBWorkspaceModeOptions } from '..';
import { translate } from '../../../common/intl';
import { companySelectors } from '../../../state/ducks/company';
import { getValidSizedFiles } from '../../components/common/attachment.field/utils';
import { toastError } from '../../components/notifications';

export const withFBMediaAlbumField = <T extends FBMediaAlbumFieldProps>(
  Component: React.FunctionComponent<T>,
) => {
  const Comp = ({
    mediaAlbumFieldState,
    setMediaFieldMode,
    onFileUploadChange,
    isTemplate = false,
    actionsDisabled,
    reviseDisabled,
    loading,
    collection,
    removeMedia,
    editorConfig,
    index,
    name = '',
    qrValue = '',
    mode,
    type,
    fieldType,
    autosave = true,
    removedDeviationIds,
    ...props
  }: T) => {
    const { workspaceState, formState, mediaAlbumState, dialogState } = FB.useStores();
    const { document: { id = '' } = {} } = workspaceState || {};
    const formValue = formState?.getFieldValue(name) || editorConfig;
    const ids = map(formValue, 'id');
    const redlineActive = useSelector(companySelectors.getRedlineActive);

    const {
      id: documentRevisionId,
      company: { companyMine: { id: companyId = undefined } = {} } = {},
    } = workspaceState || {};
    mediaAlbumFieldState = useMemo(() => new FBMediaAlbumFieldState(
      formValue,
      () => workspaceState?.id === documentRevisionId && (workspaceState?.document?.id ?? '') === id,
    // Field values seed each document instance; later updates use the synchronization reaction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [workspaceState, formState, mediaAlbumState, dialogState, documentRevisionId, id, name, isTemplate]);

    if (documentRevisionId && companyId) {
      qrValue = JSON.stringify({
        companyId,
        documentRevisionId,
        fieldName: name,
        fieldType: fieldType || type,
      });
    }

    // Handle mode
    setMediaFieldMode = () => {
      const mode = mediaAlbumFieldState?.mode === 'qr' ? 'media' : 'qr';
      mediaAlbumFieldState?.setMode(mode);
    };

    // Handle image upload
    onFileUploadChange = (event: React.FormEvent<HTMLInputElement>) => {
      if (!event.currentTarget.files) {
        return;
      }
      const filesToUpload = getValidSizedFiles(event.currentTarget.files);

      const duplicateFiles = filesToUpload.filter((file) => {
        const name = file.name.substring(0, file.name.lastIndexOf('.'));
        return mediaAlbumFieldState?.existingFileNames.includes(name);
      });
      const validFiles = difference(filesToUpload, duplicateFiles);

      if (duplicateFiles.length) {
        const duplicates = duplicateFiles.map(file => file.name).join(', ');
        toastError(translate('common.attachment.duplicate.error', { duplicates }));
      }
      mediaAlbumFieldState?.upload(validFiles);
    };

    useEffect(() => {
      const state = mediaAlbumFieldState!;
      const isCurrentDocument = () => workspaceState?.id === documentRevisionId
        && (workspaceState?.document?.id ?? '') === id;
      const disposers = [
        reaction(
          () => workspaceState?.formInputSync?.get(name),
          (value) => {
            if (!isCurrentDocument()) { return; }
            const syncValue = map(value, (syncFile) => syncFile.id ? toJS(syncFile.id) : syncFile);
            const filesIds = map(state.files, 'id');
            const addedFiles: string[] = [];
            state.fromUpload = false;
            map(union(filesIds, syncValue), (fileId) => {
              if (!syncValue.includes(fileId)) {
                state.removeFile(fileId);
              } else {
                addedFiles.push(fileId);
              }
            });
            if (addedFiles.length) { state.getFiles(addedFiles); }
          },
        ),
        reaction(
          () => state.attachmentApi.data,
          (data) => {
            if (!data || !isCurrentDocument()) { return; }
            state.fromUpload = true;
            state.download(data, dialogState?.open);
          },
        ),
        reaction(
          // Refresh duplicate-name checks after the uploaded attachments are saved.
          () => workspaceState?.documentRevApi.loading,
          (loading) => {
            if (!loading && id && isCurrentDocument()) { state.getAllAttachments(id); }
          },
        ),
        reaction(
          () => state.files,
          (files) => {
            if (!isCurrentDocument()) { return; }
            if (isTemplate && workspaceState?.schema) {
              const currentIndex = workspaceState.schema.findIndex(item => item.name === name);
              if (currentIndex !== -1) {
                workspaceState.schema[currentIndex] = {
                  ...workspaceState.schema[currentIndex],
                  editorConfig: files,
                };
              }
            }
            formState?.setFieldValue(name, files, true, isTemplate);
            if (state.fromUpload && !state.filesUploaded.length) {
              state.fromUpload = false;
              workspaceState?.saveDocRev(formState?.getValues());
            }
          },
        ),
      ];
      if (ids.length) { state.getFiles(ids); }
      if (id) { state.getAllAttachments(id); }

      return () => {
        // Keep the files reaction alive until preview-only changes reach the form.
        if (isCurrentDocument() && state.previewOnlyIds.length) {
          state.previewOnlyIds.forEach(previewId => state.removeFile(previewId));
          mediaAlbumState?.setActiveMedia(0);
          if (workspaceState?.autosave) { workspaceState.saveDocRev(formState?.getValues()); }
        }
        state.dispose();
        disposers.forEach(dispose => dispose());
      };
    // ids initialize a new field instance; subsequent changes arrive through formInputSync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mediaAlbumFieldState, workspaceState, formState, mediaAlbumState, dialogState, documentRevisionId, id, name, isTemplate]);

    const removedIdsKey = (removedDeviationIds ?? []).join(',');
    useEffect(() => {
      mediaAlbumFieldState?.getRemovedFiles(removedDeviationIds ?? []);
    // removedIdsKey stands in for the array, which is rebuilt on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mediaAlbumFieldState, removedIdsKey]);

    function isPreview (): boolean {
      if (isTemplate) {
        return ![FBWorkspaceModeOptions.DESIGN, FBWorkspaceModeOptions.FORM]
          .includes(workspaceState?.mode as FBWorkspaceModeOptions);
      }
      return workspaceState?.mode === 'preview' && workspaceState?.isOutput;
    }
    useObserver(() => {
      mode = mediaAlbumFieldState?.mode;
      loading = mediaAlbumFieldState?.loading || Boolean(mediaAlbumFieldState?.filesUploaded.length);
      const removed = mediaAlbumFieldState?.removedCollection ?? [];
      collection = removed.length ? [...(mediaAlbumFieldState?.collection ?? []), ...removed] : mediaAlbumFieldState?.collection;
    });

    if (isPreview() || !isEmpty(collection) || !isEmpty(ids)) {
      mediaAlbumFieldState?.setMode('media');
    }
    actionsDisabled = ((reviseDisabled && workspaceState?.isRevised) || isPreview()) && !redlineActive;

    return Component({
      ...(props as T),
      removeMedia: mediaAlbumFieldState?.removeFile,
      actionsDisabled,
      mediaAlbumFieldState,
      onFileUploadChange,
      setMediaFieldMode,
      reviseDisabled,
      editorConfig,
      index,
      collection,
      isTemplate,
      qrValue,
      loading,
      mode,
      name,
      autosave,
      removedDeviationIds,
    });
  };

  return (props: T) => Comp(props);
};
